import React, { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring, useReducedMotion } from 'motion/react';
import { cn } from '../../lib/utils';
import { parseFigure } from '../../lib/motion';

/*
 * Spring count-up when scrolled into view. Adapted from Magic UI "Number Ticker" (MIT)
 * via 21st.dev: https://21st.dev/dillionverma/number-ticker (upstream version, which
 * writes text directly instead of re-rendering).
 */
export const NumberTicker = ({ value, decimals = 0, grouped = true, delay = 0, className }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { damping: 60, stiffness: 100 });
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
  const format = n =>
    Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouped }).format(n);

  useEffect(() => {
    if (!inView || reduced) return undefined;
    const timer = setTimeout(() => motionValue.set(value), delay * 1000);
    return () => clearTimeout(timer);
  }, [inView, reduced, value, delay, motionValue]);

  useEffect(
    () =>
      spring.on('change', latest => {
        if (ref.current) ref.current.textContent = format(Number(latest.toFixed(decimals)));
      }),
    [spring, decimals, grouped]
  );

  // An invisible copy of the final value shares the grid cell and reserves the width, so the
  // count never shifts the layout; the count sits at the cell's end so it stays against any
  // suffix ("84%", not "84 %"). Both stay in flow (not absolute), so a parent's gradient
  // `background-clip: text` still covers the digits. Not tabular-nums: Mona Sans's tabular
  // figures have a slashed zero.
  return (
    <span className={cn('inline-grid whitespace-nowrap', className)}>
      <span className="invisible [grid-area:1/1]">{format(value)}</span>
      <span ref={ref} className="justify-self-end [grid-area:1/1]">
        {format(reduced ? value : 0)}
      </span>
    </span>
  );
};

// A figure string such as "45%", "$2M", "3x" or "50,000+" with its number counted up
const Figure = ({ value, delay = 0, className, style }) => {
  const figure = parseFigure(value);
  if (!figure) return <span className={className} style={style}>{value}</span>;
  return (
    <span className={className} style={style}>
      <span className="sr-only">{value}</span>
      <span aria-hidden="true">
        {figure.prefix}
        <NumberTicker value={figure.number} decimals={figure.decimals} grouped={figure.grouped} delay={delay} />
        {figure.suffix}
      </span>
    </span>
  );
};

export default Figure;
